/**
 * Single efficacy chip primitive (DR-7, 2026-05-13).
 *
 * 22×16 px DOM element. Type-color full-saturation background; multiplier
 * text in DM Mono 9/500 white. Used by the dossier MOVES tab right-edge
 * strip (compressed 4× / 2× / ¼× / 0× set per Premise 2 — ½× chips
 * omitted on MOVES rows; full set lives in MATCHUPS chart) AND the
 * MATCHUPS defensive chart (full set including ½×).
 *
 * The chip is purely decorative chrome; the caller decides which chips
 * to render. The primitive accepts ANY multiplier — filtering happens
 * upstream in `computeOffensiveChips` (compressed) /
 * `computeDefensiveChips` (full).
 *
 * No Phaser dependency; pure DOM. Caller is responsible for parenting +
 * tearing down (FullSheet's root.remove() in PR 2 handles cleanup
 * transitively).
 *
 * Styling lives in `apps/game/src/styles/tokens.css` under
 * `.efficacy-chip` — type color resolved via `--chip-type` custom
 * property set inline by this factory. CSS keeps text size + radius +
 * dimensions; this module owns the data binding.
 */

/** Multiplier label table — symbol form per DR-7 (× for damage, fraction
 *  glyphs for ½ / ¼, bare "0" for immunity). 4× rendered with ×; "0"
 *  intentionally lacks × so immunity reads as "completely no damage". */
function multiplierLabel(m: number): string {
  if (m === 4) return '4×';
  if (m === 2) return '2×';
  if (m === 1) return '1×';
  if (m === 0.5) return '½';
  if (m === 0.25) return '¼';
  if (m === 0) return '0';
  return `${m}×`;
}

/** Long-form description for aria-label — screen readers get sentence
 *  prose rather than a multiplier symbol. */
function multiplierAriaText(m: number): string {
  if (m === 4) return 'four times damage';
  if (m === 2) return 'super effective';
  if (m === 1) return 'neutral damage';
  if (m === 0.5) return 'resisted';
  if (m === 0.25) return 'doubly resisted';
  if (m === 0) return 'no effect';
  return `${m} times damage`;
}

export interface EfficacyChipOptions {
  /** Type the chip represents (Title-case OR lowercase; normalized
   *  internally). Background tints via `--type-X` token. */
  type: string;
  /** Multiplier the chip's label encodes. One of {0, 0.25, 0.5, 1, 2, 4}. */
  multiplier: number;
}

/**
 * Build a single `.efficacy-chip` element. Returned node is unparented
 * — caller appends to the appropriate row container.
 */
export function createEfficacyChip(opts: EfficacyChipOptions): HTMLElement {
  const el = document.createElement('span');
  el.className = 'efficacy-chip';
  el.style.setProperty('--chip-type', `var(--type-${opts.type.toLowerCase()})`);
  el.textContent = multiplierLabel(opts.multiplier);
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', `${opts.type}, ${multiplierAriaText(opts.multiplier)}`);
  return el;
}
